import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CalendarDays, ChevronLeft, ChevronRight, ListVideo, Camera } from 'lucide-react';
import { toast } from 'react-toastify';
import { getRecordingsCalendar, getRecordingsList, getSnapshot } from '../api/ropApi';
import { useIsAdmin } from '../hooks/useIsAdmin';

const GENERIC_ERROR_MESSAGE = 'No se pudo completar la operación.';

const getErrorMessage = (error) => error?.response?.data?.message || GENERIC_ERROR_MESSAGE;

const MONTH_LABELS = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

const WEEKDAY_LABELS = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];

// Backend contract only says `date` marks a day with recordings; the exact string
// format wasn't nailed down precisely, so this assumes `YYYY-MM-DD` (same
// assumption as `recordDate`/`startAt`/`endAt` below) to build/match day keys.
const pad2 = (n) => String(n).padStart(2, '0');
const toDateStr = (year, month, day) => `${year}-${pad2(month)}-${pad2(day)}`;

// ROP's calendar returns only the day of the month (1-31) per item, not a full
// date. Full YYYY-MM-DD values are kept as-is in case a firmware sends them.
const calendarItemToDateStr = (item, year, month) => {
  const raw = String(item?.date ?? '');
  return /^\d{4}-\d{2}-\d{2}$/.test(raw) ? raw : toDateStr(year, month, Number(raw));
};

const RopImagenesTab = ({ uid }) => {
  const isAdmin = useIsAdmin();
  const now = new Date();
  const [viewYear, setViewYear] = useState(now.getFullYear());
  const [viewMonth, setViewMonth] = useState(now.getMonth() + 1); // 1-12
  const [highlightedDays, setHighlightedDays] = useState(new Set());
  const [calendarLoading, setCalendarLoading] = useState(false);

  const [selectedDay, setSelectedDay] = useState(null);
  const [recordings, setRecordings] = useState([]);
  const [nextToken, setNextToken] = useState(null);
  const [dayLoading, setDayLoading] = useState(false);
  const [loadMoreLoading, setLoadMoreLoading] = useState(false);

  const [snapshotLoading, setSnapshotLoading] = useState(false);
  const [snapshotUrl, setSnapshotUrl] = useState(null);
  const snapshotUrlRef = useRef(null);

  const fetchCalendar = useCallback(async (year, month) => {
    setCalendarLoading(true);
    try {
      const data = await getRecordingsCalendar(uid, { year, month });
      setHighlightedDays(
        new Set((data?.items || []).map((item) => calendarItemToDateStr(item, year, month)))
      );
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setCalendarLoading(false);
    }
  }, [uid]);

  useEffect(() => {
    if (!uid) return;
    fetchCalendar(viewYear, viewMonth);
  }, [uid, viewYear, viewMonth, fetchCalendar]);

  // Revoke the last snapshot object URL on unmount to avoid leaking memory.
  useEffect(() => {
    return () => {
      if (snapshotUrlRef.current) {
        URL.revokeObjectURL(snapshotUrlRef.current);
      }
    };
  }, []);

  if (!uid) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
        <p className="text-sm text-gray-600 text-center">
          Esta cámara no tiene un dispositivo ROP vinculado.
        </p>
      </div>
    );
  }

  const handlePrevMonth = () => {
    if (viewMonth === 1) {
      setViewMonth(12);
      setViewYear((year) => year - 1);
    } else {
      setViewMonth((month) => month - 1);
    }
  };

  const handleNextMonth = () => {
    if (viewMonth === 12) {
      setViewMonth(1);
      setViewYear((year) => year + 1);
    } else {
      setViewMonth((month) => month + 1);
    }
  };

  const loadDayRecordings = async (dateStr, token) => {
    const setLoading = token ? setLoadMoreLoading : setDayLoading;
    setLoading(true);
    try {
      const data = await getRecordingsList(uid, {
        recordDate: dateStr,
        startAt: '00:00:00',
        endAt: '23:59:59',
        nextToken: token || null,
      });
      const items = data?.items || [];
      setRecordings((prev) => (token ? [...prev, ...items] : items));
      setNextToken(data?.nextToken || null);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleDayClick = (dateStr) => {
    if (!highlightedDays.has(dateStr)) return;
    setSelectedDay(dateStr);
    setRecordings([]);
    setNextToken(null);
    loadDayRecordings(dateStr, null);
  };

  const handleLoadMore = () => {
    if (!nextToken || !selectedDay) return;
    loadDayRecordings(selectedDay, nextToken);
  };

  const handleSnapshot = async () => {
    setSnapshotLoading(true);
    try {
      const blob = await getSnapshot(uid, {});
      const url = URL.createObjectURL(blob);
      if (snapshotUrlRef.current) {
        URL.revokeObjectURL(snapshotUrlRef.current);
      }
      snapshotUrlRef.current = url;
      setSnapshotUrl(url);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSnapshotLoading(false);
    }
  };

  const daysInMonth = new Date(viewYear, viewMonth, 0).getDate();
  const firstWeekday = new Date(viewYear, viewMonth - 1, 1).getDay(); // 0 = Sunday
  const dayCells = [
    ...Array.from({ length: firstWeekday }, () => null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-9 h-9 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center">
            <CalendarDays size={18} />
          </div>
          <h3 className="text-base font-semibold text-gray-800">Calendario de grabaciones</h3>
        </div>

        <div className="flex items-center justify-between mb-3">
          <button
            type="button"
            onClick={handlePrevMonth}
            disabled={calendarLoading}
            aria-label="Mes anterior"
            className={`p-2 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors ${
              calendarLoading ? 'opacity-60 cursor-wait' : ''
            }`}
          >
            <ChevronLeft size={16} />
          </button>
          <span className="text-sm font-semibold text-gray-800">
            {MONTH_LABELS[viewMonth - 1]} {viewYear}
          </span>
          <button
            type="button"
            onClick={handleNextMonth}
            disabled={calendarLoading}
            aria-label="Mes siguiente"
            className={`p-2 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors ${
              calendarLoading ? 'opacity-60 cursor-wait' : ''
            }`}
          >
            <ChevronRight size={16} />
          </button>
        </div>

        <div className="grid grid-cols-7 gap-1 text-center mb-1">
          {WEEKDAY_LABELS.map((label, index) => (
            <span key={`weekday-${index}`} className="text-xs text-gray-400">
              {label}
            </span>
          ))}
        </div>

        <div className={`grid grid-cols-7 gap-1 text-center ${calendarLoading ? 'opacity-60' : ''}`}>
          {dayCells.map((day, index) => {
            if (day === null) return <div key={`empty-${index}`} />;
            const dateStr = toDateStr(viewYear, viewMonth, day);
            const isHighlighted = highlightedDays.has(dateStr);
            const isSelected = selectedDay === dateStr;
            return (
              <button
                key={dateStr}
                type="button"
                disabled={!isHighlighted || calendarLoading}
                onClick={() => handleDayClick(dateStr)}
                aria-label={`Día ${day}${isHighlighted ? ' con grabaciones' : ''}`}
                aria-pressed={isSelected}
                className={`aspect-square flex items-center justify-center rounded-lg text-sm transition-colors ${
                  isSelected
                    ? 'bg-blue-600 text-white border border-blue-600'
                    : isHighlighted
                    ? 'bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 cursor-pointer'
                    : 'text-gray-400 border border-transparent cursor-default'
                }`}
              >
                {day}
              </button>
            );
          })}
        </div>
      </div>

      {selectedDay && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-9 h-9 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center">
              <ListVideo size={18} />
            </div>
            <h3 className="text-base font-semibold text-gray-800">
              Grabaciones del {selectedDay}
            </h3>
          </div>

          {dayLoading ? (
            <p className="text-sm text-gray-500 text-center py-4">Cargando grabaciones…</p>
          ) : recordings.length === 0 ? (
            <p className="text-sm text-gray-600 text-center py-4">No hay grabaciones para este día.</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {recordings.map((rec, index) => (
                <li key={`${rec.fileName}-${index}`} className="py-2 flex flex-col gap-0.5">
                  <span className="text-sm font-medium text-gray-800">{rec.fileName}</span>
                  <span className="text-xs text-gray-500">
                    {rec.startTime}–{rec.endTime} · {rec.streamType}
                  </span>
                </li>
              ))}
            </ul>
          )}

          {nextToken && !dayLoading && (
            <button
              type="button"
              onClick={handleLoadMore}
              disabled={loadMoreLoading}
              className={`mt-4 w-full rounded-lg border border-gray-200 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors ${
                loadMoreLoading ? 'opacity-60 cursor-wait' : ''
              }`}
            >
              {loadMoreLoading ? 'Cargando…' : 'Cargar más'}
            </button>
          )}
        </div>
      )}

      {/* Snapshot is not part of the non-admin feature set. */}
      {isAdmin && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-9 h-9 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center">
              <Camera size={18} />
            </div>
            <h3 className="text-base font-semibold text-gray-800">Snapshot</h3>
          </div>
          <button
            type="button"
            onClick={handleSnapshot}
            disabled={snapshotLoading}
            className={`w-full rounded-lg border border-gray-200 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors ${
              snapshotLoading ? 'opacity-60 cursor-wait' : ''
            }`}
          >
            {snapshotLoading ? 'Tomando snapshot…' : 'Tomar snapshot'}
          </button>
          {snapshotUrl && (
            <img
              src={snapshotUrl}
              alt="Snapshot de la cámara"
              className="mt-4 w-full rounded-lg border border-gray-200"
            />
          )}
        </div>
      )}
    </div>
  );
};

export default RopImagenesTab;
